import type { MutableRefObject } from 'react';
import { FC, useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';

import { useSession } from '@eneco/auth';
import type { AuthTokens, ChatbotInitOptions, Namespace } from '@eneco/chatbot-types';
import { getPendingOAuth } from '@eneco/chatbot-types';
import env, { getStaticPrefix } from '@eneco/common/env';
import logger from '@eneco/common/log';
import { useRouter } from '@eneco/next';
import { useLocale } from '@eneco/sparky';
import Script from 'next/script';
import { signIn } from 'next-auth/react';

import { chatbotScriptSrc } from './chatbotVersion';
import { ChatV2Context, ChatV2ContextValue } from './useChatV2';

interface ChatV2ProviderProps {
  children: ReactNode;
  /** Namespace used to detect pending OAuth from localStorage after redirect */
  defaultNamespace?: Namespace;
  /**
   * Whether this provider runs inside an authenticated container (with SessionProvider).
   * When false, all auth logic is skipped: no token passing, no token refresh,
   * no OAuth restore, and no onLoginRequired callback.
   * Defaults to true for backward compatibility.
   */
  authenticated?: boolean;
  /**
   * Optional URL to redirect to when the chatbot requests authentication.
   * When provided, a full-page redirect (`window.location.href`) is used instead
   * of calling `signIn('okta')`. This is useful for containers (e.g. MKB) where
   * the NextAuth sign-in flow results in a 404 after redirect.
   *
   * Example: `/mijn-eneco` — a protected route that triggers Okta auth via the
   * selfservice container.
   */
  loginRedirectUrl?: string;
}

/**
 * Delegates to the authenticated or unauthenticated implementation.
 * This split is necessary because useSession() (a React hook) cannot be called
 * conditionally. Containers without a SessionProvider (e.g. grootzakelijk) must
 * use the unauthenticated variant to avoid runtime errors from next-auth.
 */
export const ChatV2Provider: FC<ChatV2ProviderProps> = ({
  children,
  defaultNamespace,
  authenticated = true,
  loginRedirectUrl,
}) => {
  if (authenticated) {
    return (
      <AuthenticatedChatV2Provider defaultNamespace={defaultNamespace} loginRedirectUrl={loginRedirectUrl}>
        {children}
      </AuthenticatedChatV2Provider>
    );
  }
  return <UnauthenticatedChatV2Provider defaultNamespace={defaultNamespace}>{children}</UnauthenticatedChatV2Provider>;
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

interface InternalProviderProps {
  children: ReactNode;
  defaultNamespace?: Namespace;
  loginRedirectUrl?: string;
}

/**
 * Shared hook for script loading, mount queue, route-change handling, and cleanup.
 * Both authenticated and unauthenticated providers use this.
 */
const useChatV2Core = () => {
  const appLocale = useLocale();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const router = useRouter();
  const pendingMountCalls = useRef<ChatbotInitOptions[]>([]);
  const scriptSrc = chatbotScriptSrc(getStaticPrefix());

  return {
    appLocale,
    isScriptLoaded,
    setIsScriptLoaded,
    isInitialized,
    setIsInitialized,
    router,
    pendingMountCalls,
    scriptSrc,
  };
};

/**
 * Shared logic: execute a queued mount call against the chatbot SDK.
 * Auth-related init options (tokens, onTokenRefresh, callbacks) are passed in
 * by the caller so each variant can supply or omit them.
 */
const useExecuteMountCall = (
  appLocale: string,
  authInitOptions: {
    tokens?: AuthTokens;
    onTokenRefresh?: () => Promise<AuthTokens>;
    callbacks?: { onLoginRequired: () => void };
  },
  setIsInitialized: (v: boolean) => void,
) => {
  // Stable ref so the callback always sees the latest auth options without
  // re-creating itself (and invalidating downstream memoisation).
  const authRef = useRef(authInitOptions);
  useEffect(() => {
    authRef.current = authInitOptions;
  }, [authInitOptions]);

  return useCallback(
    async (args: ChatbotInitOptions) => {
      const { namespace, topic, locale = appLocale, message, visibility } = args;

      try {
        if (!window.sparkyChat?.isInitialized()) {
          if (visibility === 'minimized' && !topic && !message) {
            return;
          }

          await window.sparkyChat?.init({
            namespace,
            topic,
            message,
            visibility,
            locale,
            baseURL: `${env('DC_HOST')}/v1/orchestratoragent/nl/eneco/dxpweb`,
            apiKey: env('DC_API_KEY'),
            tokens: authRef.current.tokens,
            onTokenRefresh: authRef.current.onTokenRefresh,
            callbacks: authRef.current.callbacks,
          });
          setIsInitialized(true);
        } else {
          const shouldForceShow = visibility !== undefined && visibility !== 'idle';
          if (topic) {
            window.sparkyChat?.setTopic(topic, undefined, shouldForceShow);
          } else if (message) {
            window.sparkyChat?.sendMessage(message, shouldForceShow);
          }
        }
      } catch (error) {
        logger.error('Failed to mount ChatV2 instance', error as string);
      }
    },
    [appLocale, setIsInitialized],
  );
};

/** Shared effects & render output used by both variants. */
const useSharedEffectsAndRender = (
  isScriptLoaded: boolean,
  isInitialized: boolean,
  setIsScriptLoaded: (v: boolean) => void,
  router: ReturnType<typeof useRouter>,
  pendingMountCalls: MutableRefObject<ChatbotInitOptions[]>,
  scriptSrc: string,
  executeMountCall: (args: ChatbotInitOptions) => void,
  children: ReactNode,
) => {
  // Execute pending mount calls when script loads
  useEffect(() => {
    if (isScriptLoaded && window.sparkyChat && pendingMountCalls.current.length > 0) {
      pendingMountCalls.current.forEach(executeMountCall);
      pendingMountCalls.current = [];
    }
  }, [isScriptLoaded, executeMountCall, pendingMountCalls]);

  // Handle route change, do not destroy chatbot instance
  useEffect(() => {
    const handleRouteChange = () => {
      if (isInitialized && window.sparkyChat?.isInitialized()) {
        // The chatbot will manage its own visibility
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [isInitialized, router.events]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (window.sparkyChat?.isInitialized()) {
        window.sparkyChat.destroy();
      }
    };
  }, []);

  const mount = useCallback(
    (args: ChatbotInitOptions = {}) => {
      if (!isScriptLoaded || !window.sparkyChat) {
        pendingMountCalls.current.push(args);
        return;
      }
      executeMountCall(args);
    },
    [isScriptLoaded, executeMountCall, pendingMountCalls],
  );

  const value = useMemo<ChatV2ContextValue>(() => [mount], [mount]);

  return (
    <ChatV2Context.Provider value={value}>
      <Script src={scriptSrc} strategy="afterInteractive" onLoad={() => setIsScriptLoaded(true)} />
      {children}
    </ChatV2Context.Provider>
  );
};

// ---------------------------------------------------------------------------
// Authenticated variant — uses useSession() + signIn()
// ---------------------------------------------------------------------------

const AuthenticatedChatV2Provider: FC<InternalProviderProps> = ({ children, defaultNamespace, loginRedirectUrl }) => {
  const { data: session } = useSession();
  const {
    appLocale,
    isScriptLoaded,
    setIsScriptLoaded,
    isInitialized,
    setIsInitialized,
    router,
    pendingMountCalls,
    scriptSrc,
  } = useChatV2Core();

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const buildAuthTokens = useCallback((sessionData: typeof session): AuthTokens | null => {
    if (!sessionData?.accessToken || !sessionData?.idToken) {
      return null;
    }
    return {
      accessToken: sessionData.accessToken,
      idToken: sessionData.idToken,
      expires: sessionData.expires || '',
    };
  }, []);

  const authInitOptions = useMemo(
    () => ({
      tokens: buildAuthTokens(sessionRef.current) ?? undefined,
      onTokenRefresh: () => {
        const tokens = buildAuthTokens(sessionRef.current);
        return Promise.resolve(tokens!);
      },
      callbacks: {
        onLoginRequired: () => {
          if (loginRedirectUrl) {
            window.location.href = loginRedirectUrl;
          } else {
            signIn('okta');
          }
        },
      },
    }),
    [buildAuthTokens, loginRedirectUrl],
  );

  const executeMountCall = useExecuteMountCall(appLocale, authInitOptions, setIsInitialized);

  // Sync tokens when session changes (after initialization)
  useEffect(() => {
    if (isScriptLoaded && isInitialized && window.sparkyChat) {
      const tokens = buildAuthTokens(session);
      if (tokens) {
        window.sparkyChat.setAuthTokens(tokens);
      }
    }
  }, [session, isScriptLoaded, isInitialized, buildAuthTokens]);

  // Auto-restore chatbot after OAuth redirect
  // When the user is redirected to Okta for login and returns, the page fully reloads.
  // The chatbot library saves pending OAuth data (sasUrl + visibility) to localStorage
  // before the redirect. On return, we detect this data and auto-initialize the chatbot
  // so it can resume the conversation and complete the OAuth token exchange.
  //
  // Race condition fix: The Chat Sitecore component (V1) may initialize the chatbot
  // via ContextBridge before this effect runs (React runs child effects first).
  // The bridge defaults undefined visibility to 'idle', so the chatbot may already
  // be initialized — or init may be in progress — with the wrong visibility.
  // We always call setVisibility() after ensuring init is complete to guarantee
  // the correct visibility from pendingOAuth is applied.
  useEffect(() => {
    if (!isScriptLoaded || !window.sparkyChat || !defaultNamespace) {
      return;
    }

    const pendingOAuth = getPendingOAuth(defaultNamespace);
    if (!pendingOAuth) {
      return;
    }

    if (!session?.accessToken || !session?.idToken) {
      return;
    }

    const targetVisibility = pendingOAuth.visibility || 'compact';

    const restoreAfterOAuth = async () => {
      try {
        await executeMountCall({
          namespace: defaultNamespace,
          visibility: targetVisibility,
        });

        if (window.sparkyChat?.isInitialized()) {
          window.sparkyChat.setVisibility(targetVisibility);
        }
      } catch (error) {
        logger.error('Failed to restore chatbot after OAuth redirect', error as string);
      }
    };

    restoreAfterOAuth();
  }, [isScriptLoaded, isInitialized, defaultNamespace, session, executeMountCall]);

  return useSharedEffectsAndRender(
    isScriptLoaded,
    isInitialized,
    setIsScriptLoaded,
    router,
    pendingMountCalls,
    scriptSrc,
    executeMountCall,
    children,
  );
};

// ---------------------------------------------------------------------------
// Unauthenticated variant — no useSession(), no signIn(), no OAuth restore
// ---------------------------------------------------------------------------

const UnauthenticatedChatV2Provider: FC<InternalProviderProps> = ({ children, defaultNamespace }) => {
  const {
    appLocale,
    isScriptLoaded,
    setIsScriptLoaded,
    isInitialized,
    setIsInitialized,
    router,
    pendingMountCalls,
    scriptSrc,
  } = useChatV2Core();

  const emptyAuthOptions = useMemo(() => ({}), []);
  const executeMountCall = useExecuteMountCall(appLocale, emptyAuthOptions, setIsInitialized);

  return useSharedEffectsAndRender(
    isScriptLoaded,
    isInitialized,
    setIsScriptLoaded,
    router,
    pendingMountCalls,
    scriptSrc,
    executeMountCall,
    children,
  );
};
